import React from "react";
import InfoSvg from "components/icons/24/notifications/info.svg";
import ActionReset from "components/icons/24/actions/action-reset.svg";
import ActionPause from "components/icons/24/actions/action-pause.svg";
import ActionRefresh from "components/icons/24/actions/action-refresh.svg";
import ActionClose from "components/icons/24/actions/action-close.svg";
import ActionExternalLink from "components/icons/24/actions/action-external-link.svg";
import { ActionMenuItem } from "components/elements/Dropdown/DropdownActionMenu";
import ListItemWithContext from "components/elements/ListItem/ListItemWithContext";
import IconBadge from "components/elements/Badge/IconBadge";
import AssetTypeIcon from "components/elements/Chip/AssetTypeIcon";
import { IngressListItemModel } from "./lib/IngressListItemModel";
import { getFileExtension } from "lib/actions/files";
import { AssetCategory } from "lib/types/assets/AssetCategory";
import { AssetIngressModel } from "lib/types/assets/AssetIngressModel";
import { UploadStatus } from "lib/types/UploadStatus";
import IngressStatusChip from "components/elements/Chip/IngressStatusChip";
import ProgressBarDeterminate from "components/elements/Loading/ProgressBarDeterminate";
import ProgressBarIndeterminate from "components/elements/Loading/ProgressBarIndeterminate";
import OverflowTooltip from "components/elements/Tooltips/OverflowTooltip";

function getDisplayStatus(ingress: IngressListItemModel) {
  if (
    ingress.ingressStatus === "awaitingProcessing" ||
    ingress.ingressStatus == "processing" ||
    ingress.ingressStatus === "done" ||
    ingress.ingressStatus === "error" ||
    ingress.ingressStatus === "partialError"
  ) {
    return ingress.ingressStatus;
  } else if (ingress.uploadStatus !== null) {
    return ingress.uploadStatus;
  }
  return UploadStatus.none;
}

function getProgressBarType(ingress: IngressListItemModel) {
  if (
    ingress.uploadStatus === UploadStatus.uploading ||
    ingress.uploadStatus === UploadStatus.paused ||
    ingress.ingressStatus === "done" ||
    ingress.ingressStatus === "error" ||
    ingress.ingressStatus === "partialError" ||
    ingress.uploadStatus === UploadStatus.error
  ) {
    return "determinate" as const;
  } else if (
    ingress.ingressStatus === "processing" ||
    ingress.ingressStatus === "awaitingProcessing" ||
    ingress.uploadStatus === UploadStatus.preparing
  ) {
    return "indeterminate" as const;
  }
  return "none" as const;
}

function getProgressTailwindColorClass(
  ingressStatus: AssetIngressModel["status"],
  uploadStatus: UploadStatus,
): string {
  if (uploadStatus === UploadStatus.preparing) {
    return "bg-purple-500";
  } else if (uploadStatus === UploadStatus.queued) {
    return "bg-purple-500";
  } else if (uploadStatus === UploadStatus.paused) {
    return "bg-gray-400";
  } else if (uploadStatus === UploadStatus.uploading) {
    return "bg-sky-500";
  } else if (ingressStatus === "awaitingProcessing") {
    return "bg-yellow-500";
  } else if (ingressStatus === "processing") {
    return "bg-amber-500";
  } else if (ingressStatus === "done") {
    return "bg-green-500";
  } else if (ingressStatus === "error" || ingressStatus === "partialError") {
    return "bg-red-500";
  }

  return "";
}

function getProgressValue(ingress: IngressListItemModel) {
  return ingress.ingressStatus === "done" ||
    ingress.ingressStatus === "error" ||
    ingress.ingressStatus === "partialError" ||
    ingress.uploadStatus === UploadStatus.error
    ? 100
    : (ingress.progress && Math.round(ingress.progress * 100)) || 0;
}

export default function IngressListItem(p: {
  readonly item: IngressListItemModel;
  readonly title?: React.ReactNode;
  readonly disableTooltip?: boolean;
  readonly isExpanded?: boolean;
  readonly hasAnyExpanded?: boolean;
  readonly onDelete?: (jobId: string) => void;
  readonly onDismiss?: (jobId: string) => void;
  readonly onPause?: (jobId: string) => void;
  readonly onResume?: (jobId: string) => void;
  readonly onCancel?: (jobId: string) => void;
  readonly onWhyDidThisHappen?: (item: IngressListItemModel) => void;
  readonly onViewModel?: (item: IngressListItemModel) => void;
}) {
  const { item } = p;

  const progressPercent = getProgressValue(p.item);
  const progressType = getProgressBarType(p.item);
  const progressColorClass = getProgressTailwindColorClass(
    p.item.ingressStatus,
    p.item.uploadStatus,
  );

  // Determine if this item should use dismiss or delete based on status
  const shouldUseDismiss =
    item.ingressStatus === "done" ||
    item.ingressStatus === "error" ||
    item.ingressStatus === "partialError";

  // Build action buttons based on upload status
  const getActionButtons = (): ActionMenuItem[] => {
    // Check if upload has failed
    const hasFailed =
      item.ingressStatus === "error" ||
      item.ingressStatus === "partialError" ||
      item.uploadStatus === UploadStatus.error;

    // For failed uploads, show "Why did this happen" and "Dismiss"
    if (hasFailed) {
      return [
        {
          leftIcon: <InfoSvg className="text-icon-normal" />,
          text: "Why did this happen",
          onClick: () => p.onWhyDidThisHappen?.(item),
          isDisabled: !p.onWhyDidThisHappen,
        },
        {
          leftIcon: <ActionReset className="text-red-500" />,
          text: "Dismiss",
          textClasses: "text-red-500",
          onClick: () => p.onDismiss?.(item.id),
          isDisabled: !p.onDismiss,
        },
      ];
    }

    // For uploads, show different actions based on upload status
    if (item.ingressSource === "browserUpload") {
      switch (item.uploadStatus) {
        case UploadStatus.uploading:
        case UploadStatus.preparing:
          return [
            {
              leftIcon: <ActionPause className="text-icon-normal" />,
              text: "Pause",
              onClick: () => p.onPause?.(item.id),
              isDisabled: !p.onPause,
            },
            {
              leftIcon: <ActionClose className="text-red-500" />,
              text: "Cancel",
              textClasses: "text-red-500",
              onClick: () => p.onCancel?.(item.id),
              isDisabled: !p.onCancel,
            },
          ];

        case UploadStatus.queued:
          return [
            {
              leftIcon: <ActionClose className="text-red-500" />,
              text: "Cancel",
              textClasses: "text-red-500",
              onClick: () => p.onCancel?.(item.id),
              isDisabled: !p.onCancel,
            },
          ];

        case UploadStatus.paused:
          return [
            {
              leftIcon: <ActionRefresh className="text-icon-normal" />,
              text: "Resume",
              onClick: () => p.onResume?.(item.id),
              isDisabled: !p.onResume,
            },
            {
              leftIcon: <ActionClose className="text-red-500" />,
              text: "Cancel",
              textClasses: "text-red-500",
              onClick: () => p.onCancel?.(item.id),
              isDisabled: !p.onCancel,
            },
          ];

        default:
          // For completed states, check if we have an associated asset
          const isSuccessfullyCompleted =
            item.ingressStatus === "done" ||
            item.uploadStatus === UploadStatus.complete;

          const hasAssociatedAsset = isSuccessfullyCompleted && item.assetId;

          if (hasAssociatedAsset) {
            return [
              {
                leftIcon: <ActionExternalLink className="text-icon-normal" />,
                text: "View Model",
                onClick: () => p.onViewModel?.(item),
                isDisabled: !p.onViewModel,
              },
              {
                leftIcon: <ActionReset className="text-red-500" />,
                text: "Dismiss",
                textClasses: "text-red-500",
                onClick: () => p.onDismiss?.(item.id),
                isDisabled: !p.onDismiss,
              },
            ];
          } else {
            return [
              {
                leftIcon: <ActionReset className="text-red-500" />,
                text: shouldUseDismiss ? "Dismiss" : "Delete",
                textClasses: "text-red-500",
                onClick: () =>
                  shouldUseDismiss
                    ? p.onDismiss?.(item.id)
                    : p.onDelete?.(item.id),
                isDisabled: false,
                showTooltip: false,
              },
            ];
          }
      }
    } else {
      // For other states, use original logic
      return [
        {
          leftIcon: <ActionReset className="text-red-500" />,
          text: shouldUseDismiss ? "Dismiss" : "Delete",
          textClasses: "text-red-500",
          onClick: () =>
            shouldUseDismiss ? p.onDismiss?.(item.id) : p.onDelete?.(item.id),
          isDisabled: false,
          showTooltip: false,
        },
      ];
    }
  };

  const buttons = getActionButtons();

  const getAssetTypeProps = () => {
    if (item.ingressSource === "autodeskImport") {
      return { category: AssetCategory.autodesk };
    }
    if (item.ingressSource === "browserUpload") {
      return { ext: getFileExtension(item.name) };
    }
    return { category: AssetCategory.bim };
  };

  const displayTitle = p.title || item.name;

  const shouldReduceOpacity = p.hasAnyExpanded && !p.isExpanded;

  return (
    <div className={shouldReduceOpacity ? "relative opacity-50" : "relative"}>
      <ListItemWithContext contextMenuItems={buttons} selected={false}>
        <IconBadge shape="circle">
          <AssetTypeIcon {...getAssetTypeProps()} />
        </IconBadge>
        <OverflowTooltip customTooltipContent={displayTitle} className="p-2">
          <div className="overflow-hidden text-ellipsis text-sm font-light">
            {displayTitle}
          </div>

          <IngressStatusChip
            status={getDisplayStatus(item)}
            progressPercent={progressPercent}
            processingDetailsMessage={item.message}
            v2={true}
          />
          <div className="relative h-0.5 w-full rounded-full bg-gray-200">
            {progressType === "determinate" && (
              <ProgressBarDeterminate
                progressPercent={progressPercent}
                progressColorClass={progressColorClass}
              />
            )}
            {progressType === "indeterminate" && (
              <ProgressBarIndeterminate
                progressColorClass={progressColorClass}
              />
            )}
          </div>
        </OverflowTooltip>
      </ListItemWithContext>
    </div>
  );
}
